import * as React from 'react';

import 'styles/global.css';

import { LocalizationProvider } from 'components/core/localization-provider';
import { ThemeProvider } from 'components/core/theme-provider/theme-provider';
import { initChartJs } from 'config/chart-js.config';
import { UserProvider } from 'contexts/user-context';
import { SnackbarProvider } from 'notistack';
import OrganisationDetails from 'pages/admin/organisations/organisation-details';
import AddUser from 'pages/admin/users/add-user';
import Plants from 'pages/client/plants/plants';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';

import AdminLayout from './pages/admin/admin-layout';
import AddFramework from './pages/admin/frameworks/add-framework';
import FrameworkDetails from './pages/admin/frameworks/framework-details';
import Frameworks from './pages/admin/frameworks/frameworks';
import AddOrganisation from './pages/admin/organisations/add-organisation';
import Organisations from './pages/admin/organisations/organisations';
import UserDetails from './pages/admin/users/user-details';
import Users from './pages/admin/users/users';
import SignIn from './pages/auth/sign-in';
import ClientLayout from './pages/client/client-layout';
import PlantDetails from './pages/client/plants/plant-details';
import AddProject from './pages/client/projects/add-project';
import ProjectDetails from './pages/client/projects/project-details';
import Projects from './pages/client/projects/projects';
import { default as ClientUserDetails } from './pages/client/users/user-details';
import NotAuthorized from './pages/errors/not-authorized';
import NotFound from './pages/errors/not-found';
import LandingPage from './pages/public/landing-page';
import { paths } from './paths';

// export const viewport = { width: 'device-width', initialScale: 1 } satisfies Viewport;

initChartJs();

const router = createBrowserRouter(
  [
    {
      id: 'root',
      path: '/',
      children: [
        {
          index: true,
          Component: LandingPage,
        },
        {
          path: paths.app.home,
          element: <Navigate to={paths.app.client.home} />,
        },
        {
          Component: ClientLayout,
          children: [
            {
              index: true,
              path: paths.app.client.home,
              element: <Navigate to={paths.app.client.projects.home} />,
            },
            {
              path: paths.app.client.projects.home,
              Component: Projects,
            },
            {
              path: paths.app.client.projects.details,
              Component: ProjectDetails,
            },
            {
              path: paths.app.client.projects.add,
              Component: AddProject,
            },
            {
              path: paths.app.client.plants.home,
              Component: Plants,
            },
            {
              path: paths.app.client.plants.details,
              Component: PlantDetails,
            },
            {
              path: paths.app.client.users.details,
              Component: ClientUserDetails,
            },
          ],
        },
        {
          Component: AdminLayout,
          children: [
            {
              index: true,
              path: paths.app.admin.home,
              element: <Navigate to={paths.app.admin.frameworks.home} />,
            },
            {
              path: paths.app.admin.frameworks.home,
              Component: Frameworks,
            },
            {
              path: paths.app.admin.frameworks.details,
              Component: FrameworkDetails,
            },
            {
              path: paths.app.admin.frameworks.add,
              Component: AddFramework,
            },
            {
              path: paths.app.admin.organisations.home,
              Component: Organisations,
            },
            {
              path: paths.app.admin.organisations.add,
              Component: AddOrganisation,
            },
            {
              path: paths.app.admin.organisations.details,
              Component: OrganisationDetails,
            },
            {
              path: paths.app.admin.users.home,
              Component: Users,
            },
            {
              path: paths.app.admin.users.add,
              Component: AddUser,
            },
            {
              path: paths.app.admin.users.details,
              Component: UserDetails,
            },
          ],
        },
        {
          path: paths.app.auth.signIn,
          Component: SignIn,
        },
        {
          path: paths.errors.notFound,
          Component: NotFound,
        },
        {
          path: paths.errors.notNotAuthorized,
          Component: NotAuthorized,
        },
      ],
    },
    {
      path: '*',
      Component: NotFound,
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true
    }
  }
);

export default function App(): React.JSX.Element {
  return (
    <>
      <SnackbarProvider autoHideDuration={3000} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} />
      <LocalizationProvider>
        <UserProvider>
          <ThemeProvider>
            <RouterProvider 
              router={router} 
              fallbackElement={<p>Initial Load...</p>} 
              future={{ v7_startTransition: true }}
            />
          </ThemeProvider>
        </UserProvider>
      </LocalizationProvider>
    </>
  );
}
